/**
 * safeAccess — 동적 키 안전 접근 유틸 (security/detect-object-injection 근본 해소)
 *
 * 문제: `obj[변수]` 동적 대괄호 접근은 키가 "__proto__"/"constructor" 등이면
 *       프로토타입 오염(prototype pollution) 위험 → eslint-plugin-security 가 경고함.
 *
 * 해법(근본): 대괄호 동적 접근을 없애고 **own 속성만** 다룸.
 *   - `Object.getOwnPropertyDescriptor` / `Object.defineProperty` 는 키를 "인자"로 받으므로
 *     (대괄호 없음) detect-object-injection 규칙에 걸리지 않음 → eslint-disable 불필요.
 *   - own 속성만 취급 → 상속 체인의 `__proto__`/`constructor` 는 무시/차단 → 오염 원천 봉쇄.
 *
 * 동작 동일성:
 *   - 반환 타입을 값 타입(`M[keyof M]` / `T`)으로 맞춰, 기존 `obj[key]`(noUncheckedIndexedAccess=false)
 *     와 동일한 타입을 유지함 → 치환 시 tsc 연쇄 에러 없음.
 *   - 정상 키는 `obj[key]` 와 같은 값, 없는 키는 `undefined`.
 */

/** 객체의 own 속성 값을 동적 키로 안전하게 읽음. `obj[key]` 대체. */
export function readKey<M extends object>(obj: M, key: PropertyKey): M[keyof M] {
  const desc = Object.getOwnPropertyDescriptor(obj, key);
  return (desc ? desc.value : undefined) as M[keyof M];
}

/** 객체가 null/undefined 일 수 있을 때(옵셔널 체이닝 `obj?.[key]` 대체). */
export function readKeyOpt<M extends object>(
  obj: M | null | undefined,
  key: PropertyKey,
): M[keyof M] | undefined {
  if (obj == null) return undefined;
  const desc = Object.getOwnPropertyDescriptor(obj, key);
  return (desc ? desc.value : undefined) as M[keyof M] | undefined;
}

/** 배열 요소를 숫자 인덱스로 안전하게 읽음. `arr[i]` 대체(숫자 인덱스는 오염 불가·안전). */
export function readIndex<T>(arr: readonly T[], index: number): T {
  return arr.at(index) as T;
}

/**
 * 배열 요소를 숫자 인덱스로 안전하게 씀. `arr[i] = val` 대체(숫자 인덱스는 오염 불가·안전).
 * `splice`(메서드 호출)로 설정해 대괄호 대입을 없앰. 범위 내 인덱스는 `arr[i]=val` 과 동일.
 */
export function setIndex<T>(arr: T[], index: number, val: T): void {
  arr.splice(index, 1, val);
}

/**
 * 객체에 동적 키로 안전하게 씀. `obj[key] = val` 대체.
 * `__proto__`/`constructor`/`prototype` 키 쓰기를 차단해 프로토타입 오염을 막고,
 * `Object.defineProperty` 로 own 데이터 속성만 설정한다(대괄호 대입 없음).
 */
export function setKey<M extends object, V>(obj: M, key: PropertyKey, val: V): void {
  if (key === "__proto__" || key === "constructor" || key === "prototype") return;
  Object.defineProperty(obj, key, {
    value: val,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}
